import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export const DEFAULT_BRANDING = {
  app_name: "VENKA World Express",
  erp_title: "Operations ERP",
  theme: "light",
  colors: { primary: "#2542E2", accent: "#4FC42C", ink: "#0A0F1F", muted: "#5A5B5E", surface: "#FFFFFF" },
  logo_url: null,
  logo_dark_url: null,
  login_tagline: { en: "", pt: "", fr: "" },
};

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const shade = (rgb, f) => rgb.map((v) => Math.max(0, Math.min(255, Math.round(v * f))));
const asVar = (rgb) => rgb.join(" ");

/** Writes the brand colours as CSS variables and the theme class on <body>. */
export function applyErpTheme(branding) {
  const b = { ...DEFAULT_BRANDING, ...(branding || {}), colors: { ...DEFAULT_BRANDING.colors, ...((branding || {}).colors || {}) } };
  const body = document.body;
  body.classList.add("erp");
  body.classList.toggle("erp-light", b.theme !== "dark");
  body.classList.toggle("erp-dark", b.theme === "dark");
  const primary = hexToRgb(b.colors.primary) || [37, 66, 226];
  const accent = hexToRgb(b.colors.accent) || [79, 196, 44];
  const ink = hexToRgb(b.colors.ink) || [10, 15, 31];
  const muted = hexToRgb(b.colors.muted) || [90, 91, 94];
  const set = (k, v) => body.style.setProperty(k, v);
  set("--venka-blue", asVar(primary));
  set("--venka-blue-dark", asVar(shade(primary, 0.78)));
  set("--venka-green", asVar(accent));
  set("--venka-green-light", asVar(accent));
  set("--venka-ink", asVar(ink));
  set("--venka-muted", asVar(muted));
  set("--erp-sidebar", asVar(ink));
  return b;
}

export function clearErpTheme() {
  const body = document.body;
  body.classList.remove("erp", "erp-light", "erp-dark");
  ["--venka-blue", "--venka-blue-dark", "--venka-green", "--venka-green-light", "--venka-ink", "--venka-muted", "--erp-sidebar"]
    .forEach((k) => body.style.removeProperty(k));
}

export const assetUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${u}` : u);

const Ctx = createContext({ branding: DEFAULT_BRANDING, company: {}, reload: () => {}, preview: () => {} });

/** Wrap ERP screens (portal layout, staff login). Loads public settings and keeps the theme applied. */
export const ErpThemeProvider = ({ children }) => {
  const [state, setState] = useState({ branding: DEFAULT_BRANDING, company: {} });

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/public`);
      setState({ branding: applyErpTheme(data.branding), company: data.company || {} });
    } catch (e) {
      applyErpTheme(DEFAULT_BRANDING);
    }
  }, []);

  // live preview from the Settings page (not saved)
  const preview = useCallback((branding) => setState((s) => ({ ...s, branding: applyErpTheme(branding) })), []);

  useEffect(() => {
    applyErpTheme(DEFAULT_BRANDING);
    reload();
    return () => clearErpTheme();
  }, [reload]);

  return <Ctx.Provider value={{ ...state, reload, preview }}>{children}</Ctx.Provider>;
};

export const useErpTheme = () => useContext(Ctx);
